import React, { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Building2, Palette, DollarSign, Save, Globe, Copy, Check, Printer } from "lucide-react";

export default function Settings() {
  const { company, refreshCompany } = useAuth();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const publicUrl = company ? `${window.location.origin}/book/${company.id}` : "";

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(publicUrl); setCopied(true); toast.success("Lien copié"); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error("Impossible de copier"); }
  };

  useEffect(() => { if (company) setForm({ ...company }); }, [company]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, vat_rate: Number(form.vat_rate || 0), reservation_fee: Number(form.reservation_fee || 0), realtime_fee: Number(form.realtime_fee || 0) };
      await api.patch("/company", payload);
      toast.success("Paramètres enregistrés");
      refreshCompany();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!company) return null;

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold">Paramètres</h1>
          <p className="text-sm text-slate-500">Entreprise, finance et apparence</p>
        </div>
        <Button onClick={save} disabled={saving} className="btn-pill bg-orange-500 hover:bg-orange-600" data-testid="save-settings-btn"><Save className="w-4 h-4 mr-1" /> {saving ? "Enregistrement…" : "Enregistrer"}</Button>
      </div>

      <Card>
        <CardHeader><CardTitle className="font-display flex items-center gap-2"><Globe className="w-5 h-5 text-orange-500" /> Lien de réservation client</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-slate-500">Partagez ce lien avec vos clients pour qu'ils achètent leurs billets en ligne (paiement électronique uniquement — Wave, Fusion, Ciné Pay, Pay Tech).</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input value={publicUrl} readOnly className="font-mono text-xs" data-testid="public-link-input" />
            <Button onClick={copyLink} className="btn-pill bg-orange-500 hover:bg-orange-600 shrink-0" data-testid="copy-public-link-btn">
              {copied ? <><Check className="w-4 h-4 mr-1" /> Copié</> : <><Copy className="w-4 h-4 mr-1" /> Copier</>}
            </Button>
            <a href={publicUrl} target="_blank" rel="noreferrer" data-testid="open-public-link">
              <Button variant="outline" className="btn-pill w-full sm:w-auto"><Globe className="w-4 h-4 mr-1" /> Ouvrir</Button>
            </a>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/company/poster.pdf`} target="_blank" rel="noreferrer" data-testid="download-poster-btn">
              <Button variant="outline" className="btn-pill w-full sm:w-auto bg-slate-900 hover:bg-slate-800 text-white border-slate-900"><Printer className="w-4 h-4 mr-1" /> Affiche A4</Button>
            </a>
          </div>
          <div className="text-xs text-slate-500">💡 Astuce : téléchargez l'affiche A4 avec QR code prêt à imprimer et à coller au guichet.</div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="font-display flex items-center gap-2"><Building2 className="w-5 h-5 text-orange-500" /> Entreprise</CardTitle></CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-4">
          <div><Label>Nom</Label><Input value={form.name || ""} onChange={set("name")} data-testid="setting-name" /></div>
          <div><Label>Email</Label><Input value={form.email || ""} onChange={set("email")} data-testid="setting-email" /></div>
          <div><Label>Téléphone</Label><Input value={form.phone || ""} onChange={set("phone")} data-testid="setting-phone" /></div>
          <div><Label>Adresse</Label><Input value={form.address || ""} onChange={set("address")} data-testid="setting-address" /></div>
          <div className="md:col-span-2"><Label>Description</Label><Textarea value={form.description || ""} onChange={set("description")} data-testid="setting-desc" /></div>
          <div><Label>URL du logo</Label><Input value={form.logo_url || ""} onChange={set("logo_url")} placeholder="https://…" data-testid="setting-logo" /></div>
          <div><Label>URL de la bannière</Label><Input value={form.banner_url || ""} onChange={set("banner_url")} placeholder="https://…" data-testid="setting-banner" /></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="font-display flex items-center gap-2"><DollarSign className="w-5 h-5 text-orange-500" /> Finance</CardTitle></CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Devise</Label>
            <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
              <SelectTrigger data-testid="setting-currency"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="FCFA">FCFA (BCEAO / BEAC)</SelectItem>
                <SelectItem value="€">Euro €</SelectItem>
                <SelectItem value="$">Dollar $</SelectItem>
                <SelectItem value="£">Livre £</SelectItem>
                <SelectItem value="¥">Yen ¥</SelectItem>
                <SelectItem value="¢">Cent ¢</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Taux TVA (%)</Label><Input type="number" value={form.vat_rate ?? 0} onChange={set("vat_rate")} data-testid="setting-vat" /></div>
          <div><Label>Frais de réservation</Label><Input type="number" value={form.reservation_fee ?? 0} onChange={set("reservation_fee")} data-testid="setting-fee-reservation" /></div>
          <div><Label>Frais billet temps réel</Label><Input type="number" value={form.realtime_fee ?? 0} onChange={set("realtime_fee")} data-testid="setting-fee-realtime" /></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="font-display flex items-center gap-2"><Palette className="w-5 h-5 text-orange-500" /> Apparence</CardTitle></CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Couleur principale</Label>
            <div className="flex gap-2 items-center mt-1">
              <input type="color" value={form.primary_color || "#F97316"} onChange={set("primary_color")} className="w-14 h-10 rounded-lg border border-slate-200 dark:border-slate-800" data-testid="setting-primary-color" />
              <Input value={form.primary_color || ""} onChange={set("primary_color")} className="max-w-[120px]" />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
